import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Divider,
  Avatar,
  Card,
  CardContent,
  TextField,
  useTheme,
} from "@mui/material";
import {
  Edit as EditIcon,
  Star as StarIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  TrendingUp as TrendingUpIcon,
  Shield as ShieldIcon,
} from "@mui/icons-material";
import {
  getRiskColor,
  getRiskTextColor,
  validateOverride,
} from "../../../../utils/riskUtils";
import { useQuery, useMutation } from "@apollo/client";
import {
  UPDATE_RISK,
  UPDATE_RISK_ASSESSMENT_ITEM,
} from "../../../../../../../apis/organization-apis/risk-apis/riskApis";
import { GET_STAFF_DETAILS } from "../../../../../../../apis/organization-apis/people-apis/peopleApis";
import { GET_MASTER_LOOKUP_BY_TYPE } from "../../../../../../../apis/common-apis/commonApis";
import ConfirmSnackbar from "../../../../../../../components/Snackbar/ConfirmSnackbar";
import { LevelSelectForm } from "../../../../../../../components/LevelSelectForm/LevelSelectForm";
import {
  EmptyFields,
  MasterLookupItemsType,
} from "../../../../../../../utils/enums";
import Role from "../../../../../../../utils/roles";
import { styles } from "../../../../styles/RiskManagement.styles";
import { formatDate } from "../../../../../../../utils/dateUtils";
import AssessmentOverrideModal from "./AssessmentOverrideModal";
import { RiskScoreType, TreatmentStatus } from "../../../../utils/enums";

export default function OverviewTab({
  risk,
  isEditable = true,
  refetchItems,
  refetchLiveRisk,
  onClose,
}) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isEditingOwner, setIsEditingOwner] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState(risk?.risk_owner_email || "");
  const [overviewComment, setOverviewComment] = useState(risk.comments || "");
  const [assessmentSnackbar, setAssessmentSnackbar] = useState(null);
  const [updateRisk, { loading: updatingOwner }] = useMutation(UPDATE_RISK);
  const [updateRiskAssessmentItem] = useMutation(UPDATE_RISK_ASSESSMENT_ITEM);
  const { data: staffData, loading: staffLoading } = useQuery(GET_STAFF_DETAILS);
  const { data: treatmentData } = useQuery(GET_MASTER_LOOKUP_BY_TYPE, {
    variables: {
      type: MasterLookupItemsType.RISK_TREATMENT,
    },
  });
  const treatments = treatmentData?.getMasterLookupByType?.data || [];
  const theme = useTheme();

  const adminEmails =
    staffData?.getEmployees?.data
      ?.filter((employee) =>
        employee?.roles?.some(
          (role) => role?.role_description === Role.ADMIN
        )
      )
      .map((employee) => employee?.emails?.[0]?.email)
      .filter(Boolean) || [];

  useEffect(() => {
    setOwnerEmail(risk?.risk_owner_email || "");
    setIsEditingOwner(false);
  }, [risk?.risk_owner_email, risk?.id]);

  const handleEditClick = () => {
    setEditDialogOpen(true);
  };

  const handleUpdateAssessmentItem = async (assessmentItemId, updateData) => {
    try {
      const { data } = await updateRiskAssessmentItem({
        variables: {
          assessment_item_id: assessmentItemId,
          update_data: updateData,
        },
      });
      if (data?.updateRiskAssessmentItem?.success) {
        setAssessmentSnackbar({
          message: data?.updateRiskAssessmentItem?.message,
          vertical: "top",
          horizontal: "right",
        });
        if (refetchItems) refetchItems();
      } else {
        setAssessmentSnackbar({
          message:
            data?.updateRiskAssessmentItem?.message ||
            "Failed to update assessment item",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (err) {
      setAssessmentSnackbar({
        message: `Error updating assessment item: ${err.message}`,
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const handleOverrideSave = ({ residual_risk_score, treatment, comment }) => {
    const validation = validateOverride(
      residual_risk_score,
      risk?.inherent_risk_score,
      RiskScoreType.RESIDUAL
    );
    if (!validation.isValid) {
      setAssessmentSnackbar({
        message: validation.error,
        vertical: "top",
        horizontal: "right",
      });
      return;
    }
    const usedComment = comment?.trim() ? comment : overviewComment;
    const update_data = {
      treatment_id: parseInt(
        treatments.find((t) => t?.title === treatment)?.id
      ),
      residual_risk_score: parseInt(residual_risk_score),
      comments: usedComment,
    };
    handleUpdateAssessmentItem(parseInt(risk?.id), update_data);
    setEditDialogOpen(false);
  };

  const handleOverrideCancel = () => {
    setEditDialogOpen(false);
  };

  const handleUpdateComment = () => {
    const update_data = {
      comments: overviewComment,
    };
    handleUpdateAssessmentItem(parseInt(risk?.id), update_data);
    onClose();
  };

  const handleOwnerSave = async () => {
    if (!ownerEmail?.trim()) {
      setAssessmentSnackbar({
        message: "Risk owner email is required",
        vertical: "top",
        horizontal: "right",
      });
      return;
    }

    if (ownerEmail === risk?.risk_owner_email) {
      setIsEditingOwner(false);
      return;
    }

    try {
      const { data } = await updateRisk({
        variables: {
          risk_id: risk?.original_risk_id,
          update_data: {
            risk_owner_email: ownerEmail.trim(),
          },
        },
      });

      if (data?.updateRisk?.success) {
        setAssessmentSnackbar({
          message: "Risk owner updated successfully",
          vertical: "top",
          horizontal: "right",
        });
        setIsEditingOwner(false);
        if (refetchLiveRisk) refetchLiveRisk();
        if (refetchItems) refetchItems();
      } else {
        setAssessmentSnackbar({
          message: data?.updateRisk?.message || "Failed to update risk owner",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (err) {
      setAssessmentSnackbar({
        message: `Error updating risk owner: ${err.message}`,
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const handleOwnerCancel = () => {
    setOwnerEmail(risk?.risk_owner_email || "");
    setIsEditingOwner(false);
  };

  return (
    <>
      <ConfirmSnackbar snackbarInfo={assessmentSnackbar} />
      <Box
        sx={{
          p: 2,
          height: "auto",
          backgroundColor: theme.customColors.background.dialog,
        }}
      >
        <Grid container spacing={2}>
          {/* Description Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
                transition: "box-shadow 0.3s ease",
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Typography
                  variant="h5"
                  fontWeight={600}
                  sx={{ mb: 1.5, color: theme.customColors.text.strong }}
                >
                  Description
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.6,
                    color: theme.customColors.text.strong,
                    backgroundColor: theme.customColors.background.header,
                    p: 1.5,
                    borderRadius: 1,
                    border: `1px solid ${theme.customColors.border.subtle}`,
                  }}
                >
                  {risk?.risk_description || EmptyFields.DESCRIPTION}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Other Details Card */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
                transition: "box-shadow 0.3s ease",
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <InfoIcon
                    sx={{ color: theme.palette._blue.main, fontSize: 18 }}
                  />
                  <Typography
                    variant="h5"
                    fontWeight={600}
                    sx={{ color: theme.customColors.text.strong }}
                  >
                    Other Details
                  </Typography>
                </Box>

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar
                      sx={{
                        backgroundColor: theme.customColors.background.tableRow,
                        color: theme.customColors.text.secondary,
                        width: 28,
                        height: 28,
                      }}
                    >
                      <CategoryIcon sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: "10px",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Category
                      </Typography>
                      <Chip
                        label={
                          risk?.risk_category_title || EmptyFields.RISK_CATEGORY
                        }
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          borderColor: theme.customColors.border.strong,
                          color: theme.customColors.text.strong,
                          mt: 0.5,
                          alignSelf: "flex-start",
                          height: "20px",
                        }}
                      />
                    </Box>
                  </Box>

                  <Divider />

                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <Avatar
                      sx={{
                        backgroundColor: theme.customColors.background.tableRow,
                        color: theme.customColors.text.secondary,
                        width: 28,
                        height: 28,
                        mt: 0.25,
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mb: isEditingOwner ? 1 : 0,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: "10px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          Owner
                        </Typography>
                        {isEditable && !isEditingOwner && (
                          <Tooltip title="Edit risk owner" arrow>
                            <IconButton
                              size="small"
                              onClick={() => setIsEditingOwner(true)}
                              sx={{
                                width: 20,
                                height: 20,
                                color: theme.customColors.text.secondary,
                                "&:hover": {
                                  color: theme.customColors.text.strong,
                                },
                              }}
                              aria-label="Edit risk owner"
                            >
                              <EditIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      {isEditable && isEditingOwner ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <LevelSelectForm
                            label="Risk Owner Email"
                            options={adminEmails.map((email) => ({
                              value: email,
                              label: email,
                            }))}
                            value={ownerEmail}
                            onChange={setOwnerEmail}
                            disabled={staffLoading || updatingOwner}
                          />
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={handleOwnerSave}
                              disabled={staffLoading || updatingOwner}
                            >
                              Save
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={handleOwnerCancel}
                              disabled={updatingOwner}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{ color: theme.customColors.text.strong }}
                        >
                          {risk?.risk_owner_email || EmptyFields.OWNER}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Divider />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar
                      sx={{
                        backgroundColor: theme.customColors.background.tableRow,
                        color: theme.customColors.text.secondary,
                        width: 28,
                        height: 28,
                      }}
                    >
                      <ShieldIcon sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: "10px",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Treatment Status
                      </Typography>
                      <Chip
                        label={
                          risk?.treatment_title || TreatmentStatus.ACCEPTED
                        }
                        {...(risk?.treatment_title === TreatmentStatus.ACCEPTED
                          ? styles.AcceptedTreatmentStatusChip
                          : styles.TreatedTreatmentStatusChip)}
                      />
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Risk Assessment Card */}
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
                transition: "box-shadow 0.3s ease",
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <TrendingUpIcon
                    sx={{ color: theme.palette._blue.main, fontSize: 18 }}
                  />
                  <Typography
                    variant="h5"
                    fontWeight={600}
                    sx={{ color: theme.customColors.text.strong }}
                  >
                    Risk Assessment
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {/* Inherent Risk - Read Only */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: theme.customColors.background.dialog,
                        borderRadius: 2,
                        border: `1px solid ${theme.customColors.border.subtle}`,
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: theme.customColors.text.strong }}
                        >
                          Inherent Risk Score
                        </Typography>
                        <Tooltip title="Based on original assessment" arrow>
                          <StarIcon
                            sx={{
                              color: theme.customColors.text.secondary,
                              fontSize: 12,
                            }}
                          />
                        </Tooltip>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 2,
                          backgroundColor: getRiskColor(
                            risk?.inherent_risk_score
                          ),
                          borderRadius: 2,
                          border: `2px solid ${getRiskTextColor(
                            risk?.inherent_risk_score
                          )}20`,
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="h2"
                          fontWeight={800}
                          sx={{
                            color: getRiskTextColor(risk?.inherent_risk_score),
                            textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                          }}
                        >
                          {risk?.inherent_risk_score}
                        </Typography>
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{
                          textAlign: "center",
                          display: "block",
                          color: theme.customColors.text.secondary,
                          fontWeight: 500,
                          fontSize: "10px",
                        }}
                      >
                        Impact: {risk?.risk_inherent_impact} × Likelihood:{" "}
                        {risk?.risk_inherent_likelihood}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Residual Risk - Editable */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: theme.customColors.background.dialog,
                        borderRadius: 2,
                        border: `1px solid ${theme.customColors.border.subtle}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1.5,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              fontSize: "12px",
                              color: theme.customColors.text.strong,
                            }}
                          >
                            Residual Risk Score
                          </Typography>
                          {isEditable && (
                            <Tooltip title="Edit residual risk score" arrow>
                              <IconButton
                                size="small"
                                onClick={handleEditClick}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  color: theme.customColors.text.secondary,
                                  backgroundColor:
                                    theme.customColors.background.tableRow,
                                  "&:hover": {
                                    backgroundColor:
                                      theme.customColors.border.subtle,
                                    color: theme.customColors.text.strong,
                                  },
                                }}
                                aria-label="Edit residual risk"
                              >
                                <EditIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 2,
                          backgroundColor: getRiskColor(
                            risk?.residual_risk_score
                          ),
                          borderRadius: 2,
                          border: `2px solid ${getRiskTextColor(
                            risk?.residual_risk_score
                          )}20`,
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="h2"
                          fontWeight={800}
                          sx={{
                            color: getRiskTextColor(risk?.residual_risk_score),
                            textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                          }}
                        >
                          {risk?.residual_risk_score}
                        </Typography>
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{
                          textAlign: "center",
                          display: "block",
                          color: theme.customColors.text.secondary,
                          fontWeight: 500,
                          fontSize: "10px",
                        }}
                      >
                        Last Updated:
                        {formatDate(risk?.updated_at)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          {/* Overview Comment Box */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
                transition: "box-shadow 0.3s ease",
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Typography
                  variant="h5"
                  fontWeight={600}
                  sx={{ mb: 1.5, color: theme.customColors.text.strong }}
                >
                  Treatment Note
                </Typography>
                <TextField
                  label="Treatment Note"
                  value={overviewComment}
                  onChange={(e) => setOverviewComment(e.target.value)}
                  required
                  fullWidth
                  multiline
                  minRows={2}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpdateComment}
                  sx={{ my: 2, float: "right" }}
                >
                  Update Comment
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {editDialogOpen && treatments.length > 0 && (
          <AssessmentOverrideModal
            open={editDialogOpen}
            onClose={handleOverrideCancel}
            onSave={handleOverrideSave}
            isEditable={isEditable}
            risk={risk}
            treatments={treatments}
            theme={theme}
          />
        )}
      </Box>
    </>
  );
}
